import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { EMPTY, takeUntil } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ToastService } from '@libs/src/toast/toast.service';
import { GroupIntroSelectionsFormService } from '@main-client/src/app/group-intro-selections-form/group-intro-selections-form.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { ProgramMembership } from '@libs/src/models/program-membership.model';
import { Program } from '@libs/src/models/program.model';
import { getMatchExclusionsUpdate } from '@main-client/src/app/account/account.helpers';
import { MatchSelections } from '@main-client/src/app/group-intro-selections-form/match-selections';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-group-match-exclusions-drawer',
  templateUrl: './group-match-exclusions-drawer.component.html',
})
export class GroupMatchExclusionsDrawerComponent {
  @Input() account: Account;
  @Input() group: Group;
  @Input() program?: Program;
  @Input() programMembership?: ProgramMembership;
  isLoading = false;
  selections: MatchSelections = {
    customSegments: {},
    hiddenCustomSegments: {},
    roles: {},
  };

  constructor(
    private readonly destroy$: DestroySubscriptionsService,
    public drawerRef: NzDrawerRef,
    private readonly groupsBackend: GroupsBackend,
    public groupIntroSelectionsFormService: GroupIntroSelectionsFormService,
    public toastService: ToastService,
  ) {}

  selectionChange($event: MatchSelections) {
    this.selections = $event;
  }

  save(group: Group, account: Account, selection: MatchSelections) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.groupIntroSelectionsFormService
      .saveMatchExclusionsForAccount(group, account, selection)
      .pipe(
        catchError((responseError: HttpErrorResponse) => {
          this.isLoading = false;
          this.toastService.showError(getServerError(responseError));
          return EMPTY;
        }),
        tap((response: AccountGroupMembership[]) => {
          this.drawerRef.close({
            account: { ...this.account, groups: response },
          });
        }),
      )
      .subscribe();
  }

  saveMatchExclusionsForProgramMembership(
    group: Group,
    program: Program,
    programMembership: ProgramMembership,
    selections: MatchSelections,
  ) {
    const matchExclusionUpdate = getMatchExclusionsUpdate(
      programMembership.match_exclusions || [],
      selections,
    );
    this.groupsBackend.v3
      .patchGroupProgramMemberships(
        group._id,
        program._id,
        [programMembership._id],
        matchExclusionUpdate,
      )
      .pipe(
        catchError((responseError: HttpErrorResponse) => {
          this.isLoading = false;
          this.toastService.showError(getServerError(responseError));
          return EMPTY;
        }),
        tap(({ programMemberships }) => {
          this.drawerRef.close({ programMembership: programMemberships[0] });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
